"use client";

import React from "react";
import { Plus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const faqs = [{
  id: "faq-1",
  question: "How much does a gas boiler replacement cost in Dublin?",
  answer: "Boiler replacement costs vary depending on the model and installation complexity. We provide transparent, fixed-price quotes after a site survey to ensure no hidden surprises."
}, {
  id: "faq-2",
  question: "What is your typical response time for emergency plumbing?",
  answer: "We prioritize emergency calls across Dublin to address urgent issues like leaks, burst pipes, or heating failures promptly and safely."
}, {
  id: "faq-3",
  question: "Are your engineers RGI and SEAI registered?",
  answer: "Yes, all our gas engineers are RGI registered, and we are an SEAI registered installer for heat pumps. This ensures all work meets strict Irish safety standards and qualifies for available government grants."
}, {
  id: "faq-4",
  question: "Which boiler brands do you install and service?",
  answer: "We specialize in premium, reliable brands including Viessmann, Ideal, and Grant. These manufacturers offer excellent energy efficiency and reliable long-term performance."
}, {
  id: "faq-5",
  question: "Do you offer maintenance plans for commercial properties?",
  answer: "Absolutely. We provide tailored commercial plumbing and heating maintenance contracts for landlords, property managers, and businesses to ensure compliance and prevent costly system breakdowns."
}];
export default function FrequentlyAskedQuestions() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map(faq => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer
      }
    }))
  };
  return <section data-section-id="222424" id="frequently-asked-questions" className="bg-background text-foreground py-24 px-6 md:px-12">
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(faqSchema)
    }} />
      <div ref={ref} className={`mx-auto max-w-[800px] transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="mb-12 text-center">
          <h2 className="font-default text-3xl font-bold tracking-tight md:text-4xl text-primary">
            Frequently Asked Questions
          </h2>
          <p className="mt-4 text-muted-foreground font-default">
            Answers to common questions about our plumbing, boiler and heat pump services in Dublin.
          </p>
        </div>

        <Accordion type="single" collapsible className="w-full space-y-4">
          {faqs.map((faq, i) => <AccordionItem key={faq.id} value={faq.id} data-index={i} className="border-border border-b pb-2">
              <AccordionTrigger className="group py-5 hover:no-underline [&>svg]:hidden">
                <span className="flex w-full items-center justify-between text-left">
                  <h3 className="font-default text-lg font-bold pr-8 text-foreground group-hover:text-primary transition-colors">
                    {faq.question}
                  </h3>
                  <Plus className="text-muted-foreground size-5 shrink-0 transition-transform duration-300 ease-in-out group-data-[state=open]:rotate-45 group-data-[state=open]:text-ring" />
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <div className="bg-accent text-foreground mt-2 rounded-lg p-6 text-base leading-relaxed shadow-md border border-border/50">
                  {faq.answer}
                </div>
              </AccordionContent>
            </AccordionItem>)}
        </Accordion>

        <div className="mt-16 flex flex-col items-center justify-center text-center">
          <p className="mb-6 text-lg text-muted-foreground font-default">
            Need Plumbing or Heating Help?
          </p>
          <Button asChild size="lg" className="font-default font-semibold bg-primary hover:bg-primary/90 shadow-lg">
            <Link to="#get-your-free-quote-today">Request a Quote</Link>
          </Button>
        </div>
      </div>
    </section>;
}